// ─────────────────────────────────────────────────────────────────────────────
// v2 navigation map. Routes + permission modules mirror v1's TopNavbar exactly
// (same `hasPermission(module, "read")` gating) — only presentation differs.
// ─────────────────────────────────────────────────────────────────────────────

export const NAV_GROUPS = [
  {
    id: "home", label: "Dashboard", glyph: "home", tint: "blue", module: "dashboard",
    to: "/dashboard/home",
  },
  {
    id: "receivables", label: "Receivables", glyph: "invoice", tint: "blue",
    items: [
      { label: "Create Invoice", to: "/dashboard/createinvoice", glyph: "plus", module: "invoices" },
      { label: "Invoices", to: "/dashboard/viewinvoices", glyph: "invoice", module: "invoices" },
      { label: "Credit Notes", to: "/dashboard/credit-notes", glyph: "note", module: "invoices" },
      { label: "Client Ledger", to: "/dashboard/ledgerpage", glyph: "ledger", module: "ledger" },
      { label: "TDS Receivable", to: "/dashboard/tdsreceivable", glyph: "percent", module: "tds" },
      { label: "Recurring Invoices", to: "/dashboard/recurring-invoices", glyph: "repeat", module: "invoices" },
    ],
  },
  {
    id: "clients", label: "Clients", glyph: "clients", tint: "teal", module: "clients",
    to: "/dashboard/manageclients",
  },
  {
    id: "payables", label: "Payables", glyph: "cart", tint: "orange",
    items: [
      { label: "Bills & Expenses", to: "/dashboard/bills", glyph: "wallet", module: "bills" },
      { label: "Purchase Orders", to: "/dashboard/purchase-orders", glyph: "truck", module: "purchases" },
      { label: "Purchase Entry", to: "/dashboard/purchase-entry", glyph: "invoice", module: "purchases" },
      { label: "Debit Notes", to: "/dashboard/debit-notes", glyph: "note", module: "purchases" },
      { label: "Suppliers", to: "/dashboard/managesuppliers", glyph: "building", module: "suppliers" },
      { label: "Supplier Ledger", to: "/dashboard/supplier-ledger", glyph: "ledger", module: "suppliers" },
    ],
  },
  {
    id: "expenses", label: "Expenses", glyph: "wallet", tint: "red", module: "expenses",
    to: "/dashboard/expenses",
  },
  {
    id: "inventory", label: "Inventory", glyph: "box", tint: "yellow",
    items: [
      { label: "Stock", to: "/dashboard/managestock", glyph: "box", module: "stock" },
      { label: "Import Products", to: "/dashboard/importproducts", glyph: "upload", module: "stock" },
    ],
  },
  {
    id: "people", label: "People", glyph: "people", tint: "green",
    items: [
      { label: "Staff", to: "/dashboard/staff", glyph: "user", module: "staff" },
      { label: "Attendance", to: "/dashboard/staff/attendance", glyph: "calendar", module: "attendance" },
      { label: "Payroll", to: "/dashboard/staff/payroll", glyph: "cash", module: "payroll" },
    ],
  },
  {
    id: "sales", label: "Sales Team", glyph: "badge", tint: "indigo",
    items: [
      { label: "Salespeople", to: "/dashboard/sales/salespeople", glyph: "people", module: "sales" },
      { label: "Commissions", to: "/dashboard/sales/commissions", glyph: "percent", module: "sales" },
    ],
  },
  {
    id: "reports", label: "Reports", glyph: "chart", tint: "purple",
    items: [
      { label: "Reports", to: "/dashboard/reports", glyph: "chart", module: "reports" },
      { label: "Financial Reports", to: "/dashboard/financial-reports", glyph: "pie", module: "reports" },
      { label: "GST Reports", to: "/dashboard/gst-reports", glyph: "gst", module: "gst" },
    ],
  },
];

// Pages reachable from the account menu (not in the rail).
// Super admin console (v2 shell variant="admin")
export const ADMIN_GROUPS = [
  { id: "sa-overview", label: "Overview", glyph: "grid", tint: "indigo", to: "/superadmin" },
  { id: "sa-entities", label: "Entities", glyph: "building", tint: "blue", to: "/superadmin/entities" },
  { id: "sa-messages", label: "Messages", glyph: "bell", tint: "orange", to: "/superadmin/notifications" },
];

export const ACCOUNT_PAGES = [
  { label: "My Profile", to: "/dashboard/profile", glyph: "user" },
  { label: "Bank Details", to: "/dashboard/addbankdetails", glyph: "bank" },
  { label: "Team", to: "/dashboard/team", glyph: "team", adminOnly: true },
  { label: "Notifications", to: "/dashboard/notifications", glyph: "bell" },
];

// Filter a group by permission; returns null when nothing in it is visible.
export function visibleGroup(group, hasPermission) {
  if (!group.items) return hasPermission(group.module, "read") ? group : null;
  const items = group.items.filter((i) => hasPermission(i.module, "read"));
  return items.length ? { ...group, items } : null;
}

// Which group + page does a pathname belong to? (Longest-prefix match.)
export function locate(pathname, groups = NAV_GROUPS) {
  let best = null;
  const consider = (group, item, to) => {
    if (pathname === to || pathname.startsWith(to + "/")) {
      if (!best || to.length > best.to.length) best = { group, item, to };
    }
  };
  groups.forEach((g) => {
    // "/superadmin" would prefix-match everything in the console — match the overview exactly.
    if (g.to) { if (g.id === "sa-overview") { if (pathname === g.to) best = { group: g, item: null, to: g.to }; } else consider(g, null, g.to); }
    (g.items || []).forEach((i) => consider(g, i, i.to));
  });
  // Detail routes that live under a different path than their list page.
  if (!best) {
    const extra = [
      ["/dashboard/invoice/", "receivables", "Invoice"],
      ["/dashboard/products/", "inventory", "Product"],
      ["/dashboard/sales/salesperson/", "sales", "Salesperson"],
    ];
    for (const [prefix, gid, label] of extra) {
      if (pathname.startsWith(prefix)) {
        best = { group: NAV_GROUPS.find((g) => g.id === gid), item: { label }, to: prefix };
        break;
      }
    }
  }
  if (!best) {
    const acct = ACCOUNT_PAGES.find((p) => pathname.startsWith(p.to));
    if (acct) best = { group: null, item: acct, to: acct.to };
  }
  return best;
}
